import { ZONA_HORARIA } from '../config';

const LOCALE = 'es-AR';

const fmtHoraF = new Intl.DateTimeFormat(LOCALE, { timeZone: ZONA_HORARIA, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const fmtHoraSegF = new Intl.DateTimeFormat(LOCALE, {
  timeZone: ZONA_HORARIA,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});
const fmtFechaF = new Intl.DateTimeFormat(LOCALE, { timeZone: ZONA_HORARIA, day: '2-digit', month: '2-digit', year: 'numeric' });
const fmtDiaMesF = new Intl.DateTimeFormat(LOCALE, { timeZone: ZONA_HORARIA, day: '2-digit', month: '2-digit' });
const fmtFechaLargaF = new Intl.DateTimeFormat(LOCALE, {
  timeZone: ZONA_HORARIA,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const fmtDiaSemanaF = new Intl.DateTimeFormat(LOCALE, { timeZone: ZONA_HORARIA, weekday: 'short' });
const partesF = new Intl.DateTimeFormat('en-CA', {
  timeZone: ZONA_HORARIA,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const VACIO = '—';

function aFecha(iso: string | number | Date | null | undefined): Date | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const fmtHora = (iso: string | number | null | undefined): string => {
  const d = aFecha(iso);
  return d ? fmtHoraF.format(d) : VACIO;
};

export const fmtHoraSeg = (iso: string | number | Date): string => {
  const d = aFecha(iso);
  return d ? fmtHoraSegF.format(d) : VACIO;
};

export const fmtFecha = (iso: string | number | null | undefined): string => {
  const d = aFecha(iso);
  return d ? fmtFechaF.format(d) : VACIO;
};

/** "28/09 14:32" */
export const fmtFechaHora = (iso: string | number | null | undefined): string => {
  const d = aFecha(iso);
  return d ? `${fmtDiaMesF.format(d)} ${fmtHoraF.format(d)}` : VACIO;
};

/** "lunes, 28 de septiembre de 2026" a partir de "YYYY-MM-DD". */
export const fmtFechaLarga = (dia: string): string => {
  const d = new Date(`${dia}T12:00:00-03:00`);
  if (Number.isNaN(d.getTime())) return dia;
  const t = fmtFechaLargaF.format(d);
  return t.charAt(0).toUpperCase() + t.slice(1);
};

/** "lun" + "28/09" para encabezados de columnas. */
export const fmtDiaCorto = (dia: string): { semana: string; fecha: string } => {
  const d = new Date(`${dia}T12:00:00-03:00`);
  return { semana: fmtDiaSemanaF.format(d).replace('.', ''), fecha: fmtDiaMesF.format(d) };
};

export function fmtNum(n: number | null | undefined, decimales = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return VACIO;
  return new Intl.NumberFormat(LOCALE, { minimumFractionDigits: decimales, maximumFractionDigits: decimales }).format(n);
}

export const fmtKm = (n: number | null | undefined): string => (n === null || n === undefined ? VACIO : `${fmtNum(n, 1)} km`);
export const fmtKmh = (n: number | null | undefined): string => (n === null || n === undefined ? VACIO : `${fmtNum(n, 0)} km/h`);
export const fmtPct = (fraccion: number): string => `${fmtNum(Math.round(fraccion * 100))} %`;

/** Horas decimales a "9 h 30 min". */
export function fmtHorasDecimal(h: number): string {
  const total = Math.round(h * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  if (hh === 0) return `${mm} min`;
  return mm === 0 ? `${hh} h` : `${hh} h ${String(mm).padStart(2, '0')} min`;
}

export function fmtDuracionMin(min: number | null | undefined): string {
  if (min === null || min === undefined) return VACIO;
  return fmtHorasDecimal(min / 60);
}

export function fmtCoordenadas(lat: number, lon: number): string {
  return `${fmtNum(lat, 4)}, ${fmtNum(lon, 4)}`;
}

/** "hace 5 min", "hace 2 h 10 min", "ahora". */
export function fmtHace(iso: string | number | null | undefined, ahora: number = Date.now()): string {
  const d = aFecha(iso);
  if (!d) return VACIO;
  const min = Math.floor((ahora - d.getTime()) / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h ${String(min % 60).padStart(2, '0')} min`;
  return `hace ${Math.floor(h / 24)} d`;
}

export function minutosDesde(iso: string | null | undefined, ahora: number = Date.now()): number | null {
  const d = aFecha(iso);
  return d ? Math.max(0, Math.floor((ahora - d.getTime()) / 60000)) : null;
}

// ---------- Fechas de negocio (YYYY-MM-DD en hora argentina) ----------

function partes(d: Date): { y: string; m: string; d: string; hh: number; mm: number } {
  const p: Record<string, string> = {};
  for (const x of partesF.formatToParts(d)) p[x.type] = x.value;
  return { y: p.year, m: p.month, d: p.day, hh: Number(p.hour), mm: Number(p.minute) };
}

export function hoyAR(ahora: number = Date.now()): string {
  const p = partes(new Date(ahora));
  return `${p.y}-${p.m}-${p.d}`;
}

export function diaDe(iso: string | number): string {
  const p = partes(new Date(iso));
  return `${p.y}-${p.m}-${p.d}`;
}

export function sumarDias(dia: string, n: number): string {
  const d = new Date(`${dia}T12:00:00-03:00`);
  d.setUTCDate(d.getUTCDate() + n);
  return diaDe(d.getTime());
}

/** Últimos `n` días terminando en `hasta` (inclusive), de más viejo a más nuevo. */
export function ultimosDias(hasta: string, n: number): string[] {
  return Array.from({ length: n }, (_, i) => sumarDias(hasta, i - (n - 1)));
}

/** Minutos desde las 00:00 (hora argentina) del instante dado. */
export function minutosDelDia(iso: string): number {
  const p = partes(new Date(iso));
  return p.hh * 60 + p.mm;
}

/** Inicio del día argentino (00:00-03:00) como ISO UTC. */
export function inicioDiaIso(dia: string): string {
  return new Date(`${dia}T00:00:00-03:00`).toISOString();
}

export function iniciales(nombre: string): string {
  const partesNombre = nombre.trim().split(/\s+/).filter(Boolean);
  return (partesNombre[0]?.[0] ?? '?').concat(partesNombre[1]?.[0] ?? '').toUpperCase();
}
